import React from 'react';
import { CheckCircle2, Loader2, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { StopKind } from '@/lib/validation/manualStopValidation';
import type { AddressValidationResult } from '@/types/manualStops';
import { formatStandardized } from './formHelpers';

export type SuggestionChoice = 'pending' | 'accepted' | 'kept';

interface AddressVerificationProps {
  stopKind: StopKind;
  isChecking: boolean;
  result: AddressValidationResult | undefined;
  /** What the dispatcher typed, for the "Did you mean" comparison. */
  enteredAddress: string;
  suggestionChoice: SuggestionChoice;
  onAcceptSuggestion: () => void;
  onKeepEntered: () => void;
}

type Standardized = NonNullable<AddressValidationResult['standardized']>;

const Row: React.FC<{ icon: React.ReactNode; tone: string; children: React.ReactNode }> = ({
  icon,
  tone,
  children
}) => (
  <div className={`flex items-start gap-2 text-xs ${tone}`} role='status'>
    <span className='mt-0.5 shrink-0'>{icon}</span>
    <div className='min-w-0 space-y-1'>{children}</div>
  </div>
);

/** Quiet, single-line hint when USPS's residential/business flag disagrees with the stop type. */
const KindMismatchHint: React.FC<{ stopKind: StopKind; result: AddressValidationResult }> = ({
  stopKind,
  result
}) => {
  const looksBusiness = result.business === true && result.residential === false;
  const looksHome = result.residential === true && result.business === false;
  if (stopKind === 'customer' && looksBusiness) {
    return <p className='text-muted-foreground'>This looks like a business address.</p>;
  }
  if (stopKind === 'business' && looksHome) {
    return <p className='text-muted-foreground'>This looks like a residential address.</p>;
  }
  return null;
};

/**
 * Shows the USPS verification state for the selected address and the one action
 * each state needs: accept a correction, add a unit, or confirm an unverifiable address.
 */
export const AddressVerification: React.FC<AddressVerificationProps> = props => {
  const { isChecking, result, stopKind } = props;

  if (isChecking) {
    return (
      <Row icon={<Loader2 className='h-3.5 w-3.5 animate-spin' />} tone='text-muted-foreground'>
        <p>Checking address…</p>
      </Row>
    );
  }
  if (!result) return null;

  const vacantHint = result.vacant ? (
    <p className='text-muted-foreground'>This address is listed as vacant.</p>
  ) : null;

  if (
    result.status === 'corrected' &&
    props.suggestionChoice === 'pending' &&
    result.standardized
  ) {
    return <SuggestionCard {...props} standardized={result.standardized} />;
  }

  if (result.status === 'confirmed' || result.status === 'corrected') {
    return (
      <Row
        icon={<CheckCircle2 className='h-3.5 w-3.5' />}
        tone='text-green-700 dark:text-green-400'
      >
        <p>Verified address</p>
        <KindMismatchHint stopKind={stopKind} result={result} />
        {vacantHint}
      </Row>
    );
  }

  return <NeedsConfirmation status={result.status} />;
};

/** Advisory only: the dispatcher double-checks the address on their end and can still save. */
const ADVISORY: Record<'unit_missing' | 'unconfirmed' | 'unavailable', string> = {
  unit_missing:
    'This address usually needs an apartment, suite or unit number. Add it above if there is one.',
  unconfirmed: "This address couldn't be validated. Please confirm it on your end.",
  unavailable: "This address can't be validated right now. Please confirm it on your end."
};

const NeedsConfirmation: React.FC<{ status: keyof typeof ADVISORY }> = ({ status }) => (
  <Row
    icon={
      status === 'unit_missing' ? (
        <Info className='h-3.5 w-3.5' />
      ) : (
        <AlertTriangle className='h-3.5 w-3.5' />
      )
    }
    tone='text-amber-700 dark:text-amber-400'
  >
    <p>{ADVISORY[status]}</p>
  </Row>
);

const SuggestionCard: React.FC<AddressVerificationProps & { standardized: Standardized }> = ({
  standardized,
  enteredAddress,
  onAcceptSuggestion,
  onKeepEntered
}) => (
  <div className='rounded-md border bg-muted/40 p-3 space-y-2 text-xs' role='status'>
    <p className='font-medium text-foreground'>Did you mean</p>
    <p className='text-foreground'>{formatStandardized(standardized)}</p>
    <p className='text-muted-foreground'>You entered: {enteredAddress}</p>
    <div className='flex gap-2 pt-1'>
      <Button type='button' size='sm' onClick={onAcceptSuggestion}>
        Use suggested address
      </Button>
      <Button type='button' size='sm' variant='ghost' onClick={onKeepEntered}>
        Keep what I entered
      </Button>
    </div>
  </div>
);
